import { BaseViewStore } from 'core/stores';
import { action, observable } from 'mobx';
import { ProductStore } from 'modules/product/stores';
import { BaasicDropdownViewStore } from 'common/stores';
import { ApplicationStore } from 'platform/application/stores';
import { RateCalculatorForm } from 'modules/rate-calculator/forms';
import { CarrierStore } from 'modules/carrier/stores';
import { RateServiceStore } from 'modules/rate/stores';
import { carrierTypes } from 'common/constants';
import { notificationError, isSeller } from 'core/utils';
import { ModalParams } from 'core/models';
import { saveAs } from 'file-saver';
import moment from 'moment';
import XLSX from 'xlsx';
import excelJS from 'exceljs';
import _ from 'lodash';

class RateCalculatorViewStore extends BaseViewStore {
	@observable carrierTypeRateGroups = [];
	sheetColumns = [
		{ header: 'Reference', key: 'reference' },
		{ header: 'Rate zone', key: 'rateZone' },
		{ header: 'Residential recipient', key: 'residentialRecipient' },
		{ header: 'Use third party accounts', key: 'excludeThirdPartyCarriers' },
		{ header: 'Weight (LBS)', key: 'weight' },
		{ header: 'Package Length (In)', key: 'length' },
		{ header: 'Package Width (In)', key: 'width' },
		{ header: 'Package Height (In)', key: 'height' },
	];

	form = new RateCalculatorForm({
		onSuccess: async () => {
			await this.getRates();
		},
	});

	constructor(props) {
		super(props);
		this.initialize();
	}

	@action.bound
	initialize = () => {
		this.suspend();
		this.warehouseDropdownStore = new BaasicDropdownViewStore({
			field: this.form.$('warehouseId'),
			store: new ApplicationStore(this.rootStore),
			itemTemplate: {
				value: 'id',
				label: 'applicationTitle',
			},
			filterProperties: {
				warehouseOnly: true,
				isOrganizationActive: true,
				includeOnlySellerWarehouses: isSeller(this.rootStore) ? true : false,
				sellerWarehousesOnlyOrganizationId: isSeller(this.rootStore)
					? this.rootStore.applicationStore.currentOrganization.id
					: null,
				embed: ['warehouseOrganizationInfo', 'warehouseOrganizationInfo.address'],
			},
			onChangeCallback: selectedValue => {
				this.form.$('shipFromZipCode').set('value', selectedValue.extra.warehouseOrganizationInfo.address.zipCode);
			},
		});
		this.productDropdownStore = new BaasicDropdownViewStore({
			field: this.form.$('productId'),
			store: new ProductStore(this.rootStore),
			filterProperties: {
				embed: ['box'],
				isShippable: true,
			},
			onChangeCallback: this.setDimensions,
			itemTemplate: {
				value: 'id',
				label: 'mainSku',
			},
		});
		this.carrierDropdownStore = new BaasicDropdownViewStore({
			field: this.form.$('carrierIds'),
			filterProperties: {
				excludeThirdPartyCarriers: true,
			},
			store: new CarrierStore(this.rootStore),
			isMulti: true,
		});
		this.rateServiceStore = new RateServiceStore(this.rootStore);
		this.resume();
	};

	@action.bound
	getRates = async () => {
		this.suspend();
		try {
			const values = this.form.values();
			const result = await this.rateServiceStore.getRateCalculatorRates(this.createRequestBody(values));
			if (result.length > 0) {
				this.prepareData(result);
			} else {
				this.rootStore.notificationStore.error('RATECALCULATOR.NORATESFOUND');
			}
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	createRequestBody = values => {
		const { carriers, isResidentialRecipient, useThirdPartyAccounts, productId, warehouseId, ...newValues } = values;
		return {
			...newValues,
			residentialRecipient: isResidentialRecipient,
			excludeThirdPartyCarriers: !useThirdPartyAccounts,
		};
	};

	prepareData = rateGroups => {
		this.carrierTypeRateGroups = [];
		_.each(rateGroups, rateGroup => {
			rateGroup.selectedAccount = observable(rateGroup.carrierRates[0]);
			rateGroup.selectedAccount.rates = observable(rateGroup.selectedAccount.rates);
			switch (rateGroup.carrierType) {
				case carrierTypes.fedex:
					rateGroup.className = 'rate-calc__logo--fed';
					break;
				case carrierTypes.ups:
					rateGroup.className = 'rate-calc__logo--ups';
					break;
				case carrierTypes.usps:
					rateGroup.className = 'rate-calc__logo--usps';
					break;
			}
			const accountOptions = _.map(rateGroup.carrierRates, (carrierRate, idx) => {
				return {
					label: carrierRate.carrierAccount,
					value: idx,
				};
			});
			rateGroup.dropdownStore = new BaasicDropdownViewStore({
				staticOptions: accountOptions,
				value: {
					label: rateGroup.selectedAccount.carrierAccount,
					value: 0,
					extra: rateGroup.selectedAccount,
				},
				onChangeCallback: selectedValue => {
					selectedValue.extra = rateGroup.carrierRates[selectedValue.value];
				},
			});
			this.carrierTypeRateGroups.push(observable(rateGroup));
		});
	};

	@action.bound
	setDimensions = selectedValue => {
		this.form.$('weight').set('value', selectedValue.extra.weight);
		let dimensions = {};
		if (selectedValue.extra.box != null) {
			dimensions = {
				length: selectedValue.extra.box.length,
				width: selectedValue.extra.box.width,
				height: selectedValue.extra.box.height,
			};
		} else {
			dimensions = {
				length: selectedValue.extra.length,
				width: selectedValue.extra.width,
				height: selectedValue.extra.height,
			};
		}
		this.form.updateForm(dimensions);
	};

	@action.bound
	onFilesDrop = async filesMeta => {
		this.rootStore.viewStore.selectedModal.close();
		try {
			if (filesMeta && filesMeta.length == 1) {
				let fileMeta = filesMeta[0];
				let reader = new FileReader();
				if (
					fileMeta.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
					(fileMeta.type === 'application/vnd.ms-excel' && !fileMeta.name.includes('csv', fileMeta.name.length - 3))
				) {
					//Excel processing (excluding csv)
					reader.onload = async evt => {
						const workbook = XLSX.read(evt.target.result, { type: 'binary' });
						const firstWorksheet = workbook.Sheets[workbook.SheetNames[0]];
						const data = XLSX.utils.sheet_to_json(firstWorksheet);
						if (data.length > 0) {
							const requests = await this.createRateCalculatorRequests(data);
							if (requests.length > 0) {
								this.suspend();
								const result = await this.rateServiceStore.getRateCalculatorBulkRates({ requests: requests });
								if (result != null) {
									const binaryData = atob(result);
									const uint8Array = new Uint8Array(binaryData.length);
									for (let i = 0; i < binaryData.length; i++) {
										uint8Array[i] = binaryData.charCodeAt(i);
									}
									// Create a Blob from the binary data
									const blob = new Blob([uint8Array], { type: 'application/zip' });
									moment.locale(window.navigator.userLanguage || window.navigator.language);
									let localeData = moment.localeData();
									saveAs(
										blob,
										`BulkRateExport-${moment(new Date().toLocaleString())
											.tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
											.format(`${localeData.longDateFormat('L')} ${localeData.longDateFormat('LT')}`)}.zip`
									);
									this.rootStore.notificationStore.success('RATECALCULATOR.IMPORT.SUCCESS');
								} else {
									this.rootStore.notificationStore.error('RATECALCULATOR.IMPORT.NORATES');
								}
							}
						} else {
							this.rootStore.notificationStore.error(['RATECALCULATOR.IMPORT.NOVALIDDATA']);
						}
						this.resume();
					};
					try {
						reader.readAsBinaryString(fileMeta);
					} catch (err) {}
				}
			}
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	downloadTemplate = () => {
		const workbook = new excelJS.Workbook();
		workbook.created = new Date();
		workbook.modified = new Date();
		const sheet = workbook.addWorksheet('RateImportTemplate');
		sheet.columns = this.sheetColumns;
		sheet.getCell('B1').note = 'Valid values: 2, 3, 4, 5, 6, 7, 8';
		sheet.getCell('C1').note = 'Valid entry: 0,1,true,false';
		sheet.getCell('D1').note = 'Valid entry: 0,1,true,false';
		workbook.xlsx.writeBuffer().then(function(buffer) {
			const blob = new Blob([buffer], { type: 'application/xlsx' });
			saveAs(blob, 'RateImportTemplate.xlsx');
		});
	};

	@action.bound
	createRateCalculatorRequests = async data => {
		let requests = [];
		let counter = 1;
		let isFileValid = true;
		_.forEach(data, item => {
			let invalidField = '';
			this.transformData(item);
			if (item.reference == null || item.reference == '') {
				invalidField = 'Reference';
			}
			if (
				item.rateZone == null ||
				item.rateZone == '' ||
				_.findIndex([2, 3, 4, 5, 6, 7, 8], rateZone => {
					return rateZone == item.rateZone;
				}) == -1
			) {
				invalidField = 'Rate zone';
			}
			if (item.weight == null || item.weight == 0) {
				invalidField = 'Weight';
			}
			if (item.length == null || item.length == 0) {
				invalidField = 'Package length';
			}
			if (item.width == null || item.width == 0) {
				invalidField = 'Package width';
			}
			if (item.height == null || item.height == 0) {
				invalidField = 'Package height';
			}
			if (item.residentialRecipient == null || !this.validateBoolField(item.residentialRecipient)) {
				invalidField = 'Residential recipient';
			}
			if (item.excludeThirdPartyCarriers == null || !this.validateBoolField(item.excludeThirdPartyCarriers)) {
				invalidField = 'Use third party accounts';
			}
			if (invalidField == '') {
				requests.push({ ...item, excludeThirdPartyCarriers: !item.excludeThirdPartyCarriers });
			} else {
				this.rootStore.notificationStore.error([
					'RATECALCULATOR.IMPORT.INVALIDDATA',
					{ invalidField: invalidField, row: counter + 1 },
				]);
				isFileValid = false;
				invalidField = '';
			}
			counter++;
		});
		if (isFileValid) {
			return requests;
		} else {
			return [];
		}
	};

	validateBoolField = item => {
		if (item !== 0 && item !== 1 && item !== true && item !== false) {
			return false;
		} else {
			return true;
		}
	};

	transformData = item => {
		_.each(Object.keys(item), key => {
			const column = _.find(this.sheetColumns, column => {
				return key == column.header;
			});
			item[column.key] = item[key];
			delete item[key];
		});
	};
}

export default RateCalculatorViewStore;



// WEBPACK FOOTER //
// ./src/modules/rate-calculator/stores/RateCalculatorViewStore.js